import { useState, useRef, useCallback } from 'react'
import { Upload, RefreshCw, Eye, Download } from 'lucide-react'
import {
  SIMULATION_TYPES,
  applyColorBlindnessFilter,
  type SimulationType
} from './colorBlindnessUtils'

const MAX_DIMENSION = 1200 // cap to avoid slow processing on huge images

export default function ColorBlindnessSimulatorWidget() {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<SimulationType>('deuteranomaly')
  const [simulatedSrc, setSimulatedSrc] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showComparison, setShowComparison] = useState(false)
  const [comparisonPos, setComparisonPos] = useState(50) // percentage

  const fileInputRef = useRef<HTMLInputElement>(null)
  const originalImageDataRef = useRef<ImageData | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)

  const processImage = useCallback((imageData: ImageData, type: SimulationType) => {
    if (!hiddenCanvasRef.current) return
    setIsProcessing(true)

    // Use setTimeout to allow re-render before CPU-heavy work
    setTimeout(() => {
      const canvas = hiddenCanvasRef.current!
      canvas.width = imageData.width
      canvas.height = imageData.height
      const ctx = canvas.getContext('2d')!
      const filtered = applyColorBlindnessFilter(imageData, type)
      ctx.putImageData(filtered, 0, 0)
      setSimulatedSrc(canvas.toDataURL('image/png'))
      setIsProcessing(false)
    }, 10)
  }, [])

  const loadFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height)
      const imageData = ctx.getImageData(0, 0, width, height)
      originalImageDataRef.current = imageData
      setImageSrc(canvas.toDataURL('image/png'))
      setShowComparison(false)
      processImage(imageData, selectedType)
      URL.revokeObjectURL(url)
    }
    img.src = url
  }, [selectedType, processImage])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) loadFile(file)
  }, [loadFile])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) loadFile(file)
  }

  const handleTypeChange = (type: SimulationType) => {
    setSelectedType(type)
    if (originalImageDataRef.current) {
      processImage(originalImageDataRef.current, type)
    }
  }

  const handleDownload = () => {
    if (!simulatedSrc) return
    const info = SIMULATION_TYPES.find(t => t.id === selectedType)!
    const a = document.createElement('a')
    a.href = simulatedSrc
    a.download = `simulation-${info.id}.png`
    a.click()
  }

  // Slider drag for comparison view
  const handleSliderMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const onMouseMove = (ev: MouseEvent) => {
      if (!sliderRef.current) return
      const rect = sliderRef.current.getBoundingClientRect()
      const pos = Math.min(100, Math.max(0, ((ev.clientX - rect.left) / rect.width) * 100))
      setComparisonPos(pos)
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const handleSliderTouchMove = (e: React.TouchEvent) => {
    if (!sliderRef.current) return
    const rect = sliderRef.current.getBoundingClientRect()
    const pos = Math.min(100, Math.max(0, ((e.touches[0].clientX - rect.left) / rect.width) * 100))
    setComparisonPos(pos)
  }

  const selectedInfo = SIMULATION_TYPES.find(t => t.id === selectedType)!

  return (
    <div id="simulator-section" className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Color Blindness Simulator</h2>
      <p className="text-gray-600 mb-6">Upload any image to see how it appears with different types of color vision deficiency.</p>

      {/* Hidden processing canvas */}
      <canvas ref={hiddenCanvasRef} className="hidden" />
      <canvas ref={canvasRef} className="hidden" />

      {/* Upload Area */}
      {!imageSrc && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors
            ${isDragging ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'}
          `}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here</p>
          <p className="text-sm text-gray-500">or click to browse — PNG, JPG, GIF, WebP supported</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload image for color blindness simulation"
          />
        </div>
      )}

      {/* Simulation Type Selector */}
      {imageSrc && (
        <>
          <div className="mb-6">
            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-3">
                Simulation Type
              </legend>
              <div className="flex flex-wrap gap-2">
                {SIMULATION_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => handleTypeChange(type.id)}
                    className={`
                      px-3 py-1.5 rounded-full text-sm font-medium transition-colors border
                      ${selectedType === type.id
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                      }
                    `}
                    aria-pressed={selectedType === type.id}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Info bar */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <span className="font-semibold text-purple-900">{selectedInfo.label}:</span>{' '}
                <span className="text-purple-800 text-sm">{selectedInfo.description}</span>
              </div>
              <div className="text-xs text-purple-600 whitespace-nowrap">
                Affects: {selectedInfo.prevalence}
              </div>
            </div>
          </div>

          {/* Image Display */}
          <div className="mb-6">
            {!showComparison ? (
              <div className="grid md:grid-cols-2 gap-4">
                {/* Original */}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Original</p>
                  <img
                    src={imageSrc}
                    alt="Original uploaded image"
                    className="w-full rounded-lg border border-gray-200 object-contain max-h-80"
                  />
                </div>
                {/* Simulated */}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    {selectedInfo.label}
                    {isProcessing && (
                      <span className="ml-2 text-purple-500 text-xs">Processing…</span>
                    )}
                  </p>
                  {simulatedSrc && !isProcessing ? (
                    <img
                      src={simulatedSrc}
                      alt={`Image simulated with ${selectedInfo.label} color blindness`}
                      className="w-full rounded-lg border border-gray-200 object-contain max-h-80"
                    />
                  ) : (
                    <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Slider comparison */
              <div
                ref={sliderRef}
                className="relative rounded-lg overflow-hidden border border-gray-200 select-none cursor-col-resize"
                style={{ maxHeight: '400px' }}
                onMouseDown={handleSliderMouseDown}
                onTouchMove={handleSliderTouchMove}
              >
                {/* Simulated (full width behind) */}
                {simulatedSrc && (
                  <img
                    src={simulatedSrc}
                    alt={`Simulated ${selectedInfo.label}`}
                    className="w-full object-contain block"
                    style={{ maxHeight: '400px' }}
                    draggable={false}
                  />
                )}
                {/* Original clipped over left portion */}
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${comparisonPos}%` }}
                >
                  <img
                    src={imageSrc}
                    alt="Original"
                    className="w-full object-contain block"
                    style={{
                      maxHeight: '400px',
                      width: `${(100 / comparisonPos) * 100}%`,
                      maxWidth: 'none'
                    }}
                    draggable={false}
                  />
                </div>
                {/* Divider line */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white shadow-md flex items-center justify-center"
                  style={{ left: `${comparisonPos}%` }}
                >
                  <div className="w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200">
                    <span className="text-gray-600 text-xs select-none">⇔</span>
                  </div>
                </div>
                {/* Labels */}
                <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">Original</div>
                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">{selectedInfo.label}</div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              <Eye className="w-4 h-4" />
              {showComparison ? 'Side-by-Side View' : 'Slider Comparison'}
            </button>
            {simulatedSrc && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Download Simulation
              </button>
            )}
            <button
              onClick={() => {
                setImageSrc(null)
                setSimulatedSrc(null)
                originalImageDataRef.current = null
                if (fileInputRef.current) fileInputRef.current.value = ''
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Try Another Image
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload image"
          />
        </>
      )}
    </div>
  )
}
